import { Injectable, NgZone, OnDestroy } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { map } from "rxjs/operators";

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

export type InstallResult = "accepted" | "dismissed" | "ios-manual" | "desktop-manual" | "unavailable";

@Injectable({ providedIn: "root" })
export class PwaInstallService implements OnDestroy {
  private deferredPrompt: BeforeInstallPromptEvent | null = null;
  private readonly promptAvailable$ = new BehaviorSubject<boolean>(false);
  private readonly installed$ = new BehaviorSubject<boolean>(this.computeStandalone());

  private readonly onBeforeInstallPrompt = (e: Event) => {
    e.preventDefault();
    this.zone.run(() => {
      this.deferredPrompt = e as BeforeInstallPromptEvent;
      this.promptAvailable$.next(true);
    });
  };

  private readonly onAppInstalled = () => {
    this.zone.run(() => {
      this.deferredPrompt = null;
      this.promptAvailable$.next(false);
      this.installed$.next(true);
    });
  };

  /** Reagisce a cambi del display-mode (es. utente apre la PWA standalone
   *  mentre la sessione browser è ancora aperta, o il sistema operativo
   *  cambia modalità). Aggiorna `installed$` → il bottone si nasconde. */
  private readonly standaloneMql = window.matchMedia
    ? window.matchMedia("(display-mode: standalone)")
    : null;

  private readonly onStandaloneChange = (e: MediaQueryListEvent) => {
    this.zone.run(() => {
      if (e.matches) this.installed$.next(true);
    });
  };

  constructor(private zone: NgZone) {
    window.addEventListener("beforeinstallprompt", this.onBeforeInstallPrompt);
    window.addEventListener("appinstalled", this.onAppInstalled);
    if (this.standaloneMql && typeof this.standaloneMql.addEventListener === "function") {
      this.standaloneMql.addEventListener("change", this.onStandaloneChange);
    }
    this.logDiagnostics();
  }

  /** Diagnostica PWA una sola volta al boot — utile per capire perché Chrome
   *  non mostra "Installa" nel menu ⋮. Stampa manifest attivo, SW state,
   *  standalone mode e platform. */
  private logDiagnostics(): void {
    // Log diagnostico stato PWA: tenuto silenzioso di default per evitare
    // rumore in console; attivabile con `localStorage.setItem('pwaDebug','1')`.
    try {
      if (typeof localStorage === "undefined" || localStorage.getItem("pwaDebug") !== "1") return;
      const manifestLink = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
      const standalone = this.computeStandalone();
      const swSupported = "serviceWorker" in navigator;
      // eslint-disable-next-line no-console
      console.info("[PWA]", {
        manifest: manifestLink?.href ?? "(nessun link manifest in DOM)",
        path: window.location.pathname,
        standalone,
        serviceWorker: swSupported,
        iOS: this.isIOS(),
      });
      if (swSupported) {
        navigator.serviceWorker.getRegistration().then((reg) => {
          // eslint-disable-next-line no-console
          console.info("[PWA] ServiceWorker registration:", reg ? {
            scope: reg.scope,
            active: !!reg.active,
            state: reg.active?.state,
          } : "(nessun SW registrato)");
        }).catch(() => {});
      }
    } catch { /* noop */ }
  }

  ngOnDestroy(): void {
    window.removeEventListener("beforeinstallprompt", this.onBeforeInstallPrompt);
    window.removeEventListener("appinstalled", this.onAppInstalled);
    if (this.standaloneMql && typeof this.standaloneMql.removeEventListener === "function") {
      this.standaloneMql.removeEventListener("change", this.onStandaloneChange);
    }
  }

  /** True quando mostrare il bottone: l'app NON deve essere già installata
   *  (standalone mode). Doppio check: BehaviorSubject + live `isStandalone()`
   *  come belt-and-suspenders contro edge case di timing. */
  canInstall$(): Observable<boolean> {
    return this.installed$.pipe(
      map((installed) => !installed && !this.isStandalone()),
    );
  }

  isIOS(): boolean {
    const ua = window.navigator.userAgent || "";
    const iOSClassic = /iPad|iPhone|iPod/.test(ua);
    // iPadOS 13+ si presenta come Mac con touch
    const iPadOS = ua.includes("Mac") && typeof document !== "undefined" && "ontouchend" in document;
    return iOSClassic || iPadOS;
  }

  isStandalone(): boolean {
    return this.computeStandalone();
  }

  private computeStandalone(): boolean {
    const mql = window.matchMedia && window.matchMedia("(display-mode: standalone)").matches;
    const iosStandalone = (window.navigator as any).standalone === true;
    return !!(mql || iosStandalone);
  }

  /** Imposta dinamicamente il manifest PWA attivo in base al contesto
   *  (admin vs cliente). Chrome legge il manifest linkato al momento
   *  dell'install → usando `id` diversi i due contesti vengono salvati
   *  come PWA distinte sul desktop dell'utente. */
  setManifest(variant: "admin" | "cliente" | "default"): void {
    const map: Record<string, string> = {
      admin: "/assets/manifest-admin.webmanifest",
      cliente: "/assets/manifest-cliente.webmanifest",
      default: "/assets/manifest.webmanifest",
    };
    const href = map[variant];
    if (!href) return;

    let link = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "manifest";
      document.head.appendChild(link);
    }
    if (link.href.endsWith(href)) return; // già impostato
    link.href = href;
  }

  async promptInstall(): Promise<InstallResult> {
    if (this.deferredPrompt) {
      try {
        await this.deferredPrompt.prompt();
        const choice = await this.deferredPrompt.userChoice;
        const promptedEvent = this.deferredPrompt;
        this.deferredPrompt = null;
        this.promptAvailable$.next(false);
        if (choice.outcome === "accepted") {
          this.installed$.next(true);
        }
        void promptedEvent;
        return choice.outcome;
      } catch {
        this.deferredPrompt = null;
        this.promptAvailable$.next(false);
        return "unavailable";
      }
    }
    if (this.isStandalone()) {
      return "unavailable";
    }
    if (this.isIOS()) {
      return "ios-manual";
    }
    return "desktop-manual";
  }
}
